package org.lennycodes.hldi.pages
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.style.breakpoint.ResponsiveValues
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.components.AnimatedCounter
import org.lennycodes.hldi.components.GradientButton
import org.lennycodes.hldi.components.ProjectCard
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.data.Project
import org.lennycodes.hldi.layouts.MainLayout
import org.lennycodes.hldi.styles.AboutHeroSectionStyle
import org.lennycodes.hldi.styles.AboutHeroTextStyle
import org.lennycodes.hldi.styles.AboutHeroTitleStyle
import org.lennycodes.hldi.styles.DonateBtnStyle
import org.lennycodes.hldi.styles.SectionWaveSvg

val sampleProjects = listOf(
    Project(
        id = "1",
        title = "Food Security and Livelihood Resilience Project",
        location = "Biharamulo District",
        description = "This project is funded by DKA-Austria (2013–present) and implemented by the Human Life Defence Department (HLDD) of the Catholic Diocese of Rulenge-Ngara. It aims to strengthen food security and improve livelihood resilience among rural vulnerable communities.",
        imageUrl = "five.webp",
        impact = "Directly reached 2,000 people across 280 vulnerable households.",
        metrics = mapOf(
            "households" to 280,
            "beneficiaries" to 2000
        )
    ),
    Project(
        id = "2",
        title = "Menstrual Health and Hygiene in Secondary Schools",
        location = "Biharamulo District",
        description = "A three-year project designed to raise awareness among students, teachers, parents, and community leaders on menstrual health and hygiene management. The initiative aims to eliminate menstrual stigma and create a supportive learning environment where girls have equal access to education alongside boys.",
        imageUrl = "eleven.webp",
        impact = "Promoting menstrual health awareness and improving educational opportunities for school girls.",
        metrics = mapOf(
            "durationYears" to 3
        )
    ),
    Project(
        id = "3",
        title = "Improved Honey Processing Systems",
        location = "Biharamulo District",
        description = "Established by the Human Life Defence Department (HLDD) in collaboration with the Tanzania Forest Fund (TaFF), this project promotes environmentally friendly beekeeping practices while increasing the value of honey and its by-products through the installation of modern honey processing systems and honey collection centres.",
        imageUrl = "twelve.webp",
        impact = "Supporting sustainable beekeeping and improving honey production, processing, and market value.",
        metrics = mapOf(
            "processingSystems" to 1,
            "collectionCentres" to 1
        )
    ),
    Project(
        id = "4",
        title = "Women and Land Rights in Tanzania",
        location = "Biharamulo District",
        description = "A four-year project (2023–2026) implemented in partnership with ADA and BSIN through Horizont3000. The project aims to promote gender equality by advancing equal land rights and economic empowerment for women and people with disabilities.",
        imageUrl = "thirteen.webp",
        impact = "Empowering women and people with disabilities through equitable land rights and economic inclusion.",
        metrics = mapOf(
            "durationYears" to 4,
            "startYear" to 2023,
            "endYear" to 2026
        )
    ),
    Project(
        id = "5",
        title = "The Young Mothers Home Project",
        location = "Biharamulo District",
        description = "A project supporting vulnerable young mothers through safe accommodation, vocational skills, life skills education, counseling, reproductive health awareness, and entrepreneurship training to promote self-reliance and successful reintegration into their communities."
                ,imageUrl = "seventeen.webp",
        impact = "Supporting vulnerable young mothers to develop sustainable livelihoods, improve their well-being, and achieve social and economic independence.",
        metrics = mapOf(
            "durationYears" to 6,
            "startYear" to 2023,
            "endYear" to 2026
        )
    )
)

@Composable
@Page("/projects")
fun ProjectsPage() {
    MainLayout {
        // Hero
        Section(
            attrs = Modifier.padding(50.px,20.px).then( AboutHeroSectionStyle.toModifier())
                .toAttrs()
        ) {
            H1(attrs = AboutHeroTitleStyle.toAttrs()) {
                Text("Our Projects")
            }
            P(attrs = AboutHeroTextStyle.toAttrs()) {
                Text("Transforming communities through impactful projects that create lasting change.")
            }
            SectionWaveSvg()
        }

        // Impact Statistics
        Section(
            attrs = Modifier
                .margin(top = 50.px)
                .backgroundColor(HLDITheme.Colors.white)
                .toAttrs()
        ) {
            SimpleGrid(
                numColumns = ResponsiveValues(
                    1, // base (xs)
                    1, // sm
                    2, // md
                    3,  // lg+
                    4,
                    5
                ),
                modifier = Modifier.gap(32.px)
            ) {
                AnimatedCounter(7000, "Lives Impacted")
                AnimatedCounter(500, "Communities Reached")
                AnimatedCounter(5, "Active Projects")
                AnimatedCounter(1, "Volunteers")
            }
        }

        // Projects Grid
        Section(
            attrs = Modifier.margin(32.px).toAttrs()
        ) {
            SimpleGrid(
                numColumns = ResponsiveValues(
                    1, // base (xs)
                    1, // sm
                    2, // md
                    3, // lg
                    3, // xl
                    3  // 2xl+ — capped here instead of growing to 4/5
                ),
                modifier = Modifier
                    .gap(32.px)
                    .maxWidth(1440.px) // optional: stops cards stretching too wide on ultra-wide monitors
                    .margin(topBottom = 0.px, leftRight = autoLength)
            ) {
                sampleProjects.forEach { project ->
                    ProjectCard(project)
                }
            }
        }

        // Support CTA
        Section(
            attrs = {
                style {
                    background("linear-gradient(135deg, ${HLDITheme.Colors.primary} 0%, ${HLDITheme.Colors.accent} 100%)")
                    color(HLDITheme.Colors.white)
                    textAlign(TextAlign.Center)
                    padding(60.px, 20.px)
                    borderRadius(16.px)
                    margin(32.px)
                }
            }
        ) {
            H2(
                attrs = {
                    style {
                        fontSize(32.px)
                        fontFamily("Playfair Display", "serif")
                        marginBottom(16.px)
                    }
                }
            ) {
                Text("Support Our Projects")
            }
            P(
                attrs = {
                    style {
                        fontSize(16.px)
                        marginBottom(32.px)
                        opacity(0.95)
                    }
                }
            ) {
                Text("Your support enables us to continue implementing life-changing projects across the Rulenge Ngara Diocese.")
            }
            GradientButton("Donate Now", "/get-involved#donate",
                modifier = DonateBtnStyle.toModifier()
            )
        }
    }
}